'use client';
import React, { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { IntegrationBlog } from '@/constants/Integrations';
import Image from 'next/image';
import { BiCheck, BiGlobe, BiSmile, BiStar, BiTime } from 'react-icons/bi';
import { RiSparkling2Line } from 'react-icons/ri';
import { HiOutlineArrowTopRightOnSquare } from 'react-icons/hi2';
import ReactHtmlParser from 'react-html-parser';

type Props = { locale: string };

export default function VideoBotIntegration({ locale }: Props) {
  const { t } = useTranslation(locale);
  const ShowItem = IntegrationBlog.slice(0, 4);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const benefits = [
    { icon: BiTime, text: t('Integration/benefits/benefit1') },
    { icon: BiSmile, text: t('Integration/benefits/benefit2') },
    { icon: BiGlobe, text: t('Integration/benefits/benefit3') },
  ];

  const stats = [
    { value: 'Unlimited', label: t('Integration/stats/stats1') },
    { value: '100%', label: t('Integration/stats/stats2') },
    { value: 'Any Tool', label: t('Integration/stats/stats3') },
  ];

  return (
    <section className="tw-pt-20 tw-pb-2 lg:tw-pt-20 lg:tw-pb-2 tw-bg-transparent tw-font-['Lexend']">
      <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        {/* Header Section */}
        <div className="tw-text-center tw-mb-16">
          <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
            <RiSparkling2Line className="tw-w-4 tw-h-4" />
            {t('Integration/badge')}
          </div>

          <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-leading-tight">
            {t('Integration/Title')}
          </h2>

          <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto tw-leading-relaxed tw-mb-12">
            {ReactHtmlParser(t('Integration/subtitle'))}
          </p>

          {/* Benefits Row */}
          <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-6 tw-mb-16">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="tw-flex tw-items-center tw-gap-3 tw-bg-white tw-px-6 tw-py-3 tw-rounded-full tw-shadow-md tw-border tw-border-gray-100"
              >
                <benefit.icon className="tw-w-5 tw-h-5 tw-text-blue-600" />
                <span className="tw-font-medium tw-text-gray-700 tw-text-sm">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Integration Cards Grid */}
        <div className="tw-grid xl:tw-grid-cols-4 lg:tw-grid-cols-2 md:tw-grid-cols-2 tw-grid-cols-1 tw-gap-8 tw-mb-20">
          {ShowItem.map((integration, index) => (
            <div
              key={index}
              className="tw-group tw-relative tw-bg-white tw-rounded-3xl tw-shadow-lg tw-border tw-border-gray-100 tw-overflow-hidden tw-transition-all tw-duration-300 hover:tw-shadow-2xl hover:tw-scale-[1.02] tw-h-full tw-flex tw-flex-col"
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Popular Badge */}
              {integration.badge && (
                <div className="tw-absolute tw-top-4 tw-right-4 tw-z-10">
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-shadow-lg">
                    <BiStar className="tw-w-3 tw-h-3" />
                    {t(integration.badge)}
                  </span>
                </div>
              )}

              {/* Image Section */}
              <div className="tw-relative tw-p-6 tw-pb-4">
                <a href={`/${locale}/integration/${integration.name}`}>
                  <div className="tw-relative tw-w-full tw-h-[180px] tw-bg-gray-50 tw-rounded-2xl tw-overflow-hidden tw-transition-transform tw-duration-300 group-hover:tw-scale-105">
                    <Image
                      priority={index < 2}
                      quality={100}
                      className="tw-w-full tw-h-full tw-object-cover" // Changed from tw-object-contain tw-p-4 to tw-object-cover
                      src={integration.imageUrl}
                      alt={`${integration.name} integration`}
                      width={300}
                      height={180}
                    />

                    {/* Hover overlay */}
                    <div
                      className={`tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/50 tw-to-transparent tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-300 ${
                        hoveredCard === index ? 'tw-opacity-100' : 'tw-opacity-0'
                      }`}
                    >
                      <div className="tw-bg-white tw-rounded-full tw-p-3 tw-shadow-lg tw-transform tw-scale-90 group-hover:tw-scale-100 tw-transition-transform tw-duration-300">
                        <HiOutlineArrowTopRightOnSquare className="tw-w-6 tw-h-6 tw-text-blue-600" />
                      </div>
                    </div>
                  </div>
                </a>
              </div>

              {/* Content Section */}
              <div className="tw-flex tw-flex-col tw-flex-grow tw-px-6 tw-pb-6">
                <div className="tw-flex-grow">
                  <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-3 tw-leading-tight tw-group-hover:tw-text-blue-900 tw-transition-colors tw-duration-300">
                    {t(integration.title)}
                  </h3>

                  <p className="tw-text-gray-600 tw-text-sm tw-leading-relaxed tw-mb-6 tw-line-clamp-3">
                    {t(integration.shortDescription.description)}
                  </p>
                </div>

                {/* Features list */}
                <div className="tw-space-y-2 tw-mb-6">
                  {integration.checkFeatures?.map((feature: string, featureIndex: number) => (
                    <div key={featureIndex} className="tw-flex tw-items-center tw-gap-2">
                      <BiCheck className="tw-w-4 tw-h-4 tw-text-green-500 tw-flex-shrink-0" />
                      <span className="tw-text-xs tw-text-gray-600">{t(feature)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom accent */}
              <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-1 tw-bg-gradient-to-r tw-from-blue-500 tw-to-purple-500 tw-transform tw-scale-x-0 group-hover:tw-scale-x-100 tw-transition-transform tw-duration-300 tw-origin-left"></div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-8 tw-mb-16 tw-py-12 tw-border-y tw-border-gray-200">
          {stats.map((stat, index) => (
            <div key={index} className="tw-text-center">
              <div className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-2">{stat.value}</div>
              <div className="tw-text-sm tw-text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
